import EnvironmentService from '@/env/EnvironmentService';

type RenewEventType = 'renew-start' | 'renew-success' | 'renew-failed' | 'renew-release';

type RenewWaitResult = 'success' | 'failed' | 'released' | 'timeout' | 'disabled';

type RenewLockPayload = {
  lockId: string;
  ownerTabId: string;
  createdAt: number;
  updatedAt: number;
  expiresAt: number;
};

type RenewEventPayload = {
  type: RenewEventType;
  lockId: string;
  tabId: string;
  at: number;
};

const LOCK_STORAGE_KEY = 'auth:cross-tab-renew-lock';
const EVENT_STORAGE_KEY = 'auth:cross-tab-renew-event';
const TAB_ID_STORAGE_KEY = 'auth:cross-tab-tab-id';
const CHANNEL_NAME = 'auth-cross-tab-renew';

const DEFAULT_LEASE_MS = 25_000;
const DEFAULT_HEARTBEAT_MS = 5_000;
const DEFAULT_WAIT_MS = 12_000;

const isBrowser = () => typeof window !== 'undefined';

const now = () => Date.now();

const tryParseJson = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const createId = () => `${now()}-${Math.random().toString(36).slice(2, 10)}`;

class CrossTabRenewCoordinator {
  private readonly tabId: string;
  private readonly channel: BroadcastChannel | null;

  constructor() {
    this.tabId = this.resolveTabId();
    this.channel = this.createChannel();
  }

  public isEnabled(): boolean {
    return isBrowser() && EnvironmentService.Environment.isCrossTabRenewLockEnabled();
  }

  public acquireLock(leaseMs: number = DEFAULT_LEASE_MS): { acquired: boolean; lockId?: string } {
    if (!this.isEnabled()) {
      return { acquired: true, lockId: createId(), };
    }

    this.clearExpiredLock();
    const activeLock = this.readLock();
    if (activeLock && activeLock.expiresAt > now()) {
      if (activeLock.ownerTabId !== this.tabId) {
        return { acquired: false, };
      }
      return { acquired: true, lockId: activeLock.lockId, };
    }

    const lockId = createId();
    const timestamp = now();
    const nextLock: RenewLockPayload = {
      lockId,
      ownerTabId: this.tabId,
      createdAt: timestamp,
      updatedAt: timestamp,
      expiresAt: timestamp + leaseMs,
    };

    this.writeLock(nextLock);
    const confirmed = this.readLock();
    if (!confirmed || confirmed.lockId !== lockId || confirmed.ownerTabId !== this.tabId) {
      return { acquired: false, };
    }

    this.publish('renew-start', lockId);
    return { acquired: true, lockId, };
  }

  public refreshLock(lockId: string, leaseMs: number = DEFAULT_LEASE_MS): boolean {
    if (!this.isEnabled()) {
      return false;
    }

    const lock = this.readLock();
    if (!lock || lock.lockId !== lockId || lock.ownerTabId !== this.tabId) {
      return false;
    }

    const timestamp = now();
    const refreshedLock: RenewLockPayload = {
      ...lock,
      updatedAt: timestamp,
      expiresAt: timestamp + leaseMs,
    };
    this.writeLock(refreshedLock);
    return true;
  }

  public startHeartbeat(lockId: string, leaseMs: number = DEFAULT_LEASE_MS): () => void {
    if (!this.isEnabled()) {
      return () => {};
    }

    const timer = window.setInterval(() => {
      this.refreshLock(lockId, leaseMs);
    }, DEFAULT_HEARTBEAT_MS);

    return () => window.clearInterval(timer);
  }

  public publishSuccess(lockId: string): void {
    this.publish('renew-success', lockId);
  }

  public publishFailure(lockId: string): void {
    this.publish('renew-failed', lockId);
  }

  public releaseLock(lockId: string): void {
    if (!this.isEnabled()) {
      return;
    }

    const lock = this.readLock();
    if (lock && lock.lockId === lockId && lock.ownerTabId === this.tabId) {
      localStorage.removeItem(LOCK_STORAGE_KEY);
    }

    this.publish('renew-release', lockId);
  }

  public releaseOwnedLock(): void {
    if (!this.isEnabled()) {
      return;
    }

    const lock = this.readLock();
    if (!lock || lock.ownerTabId !== this.tabId) {
      return;
    }

    localStorage.removeItem(LOCK_STORAGE_KEY);
    this.publish('renew-release', lock.lockId);
  }

  public async waitForRenewResult(timeoutMs: number = DEFAULT_WAIT_MS): Promise<RenewWaitResult> {
    if (!this.isEnabled()) {
      return 'disabled';
    }

    this.clearExpiredLock();
    if (!this.hasActiveLock()) {
      return 'released';
    }

    return new Promise((resolve) => {
      let completed = false;

      const finish = (result: RenewWaitResult) => {
        if (completed) {
          return;
        }
        completed = true;
        cleanup();
        resolve(result);
      };

      const handleEventPayload = (payload: RenewEventPayload | null) => {
        if (!payload || payload.tabId === this.tabId) {
          return;
        }

        if (payload.type === 'renew-success') {
          finish('success');
          return;
        }

        if (payload.type === 'renew-failed') {
          finish('failed');
          return;
        }

        if (payload.type === 'renew-release') {
          finish('released');
        }
      };

      const messageHandler = (event: MessageEvent<RenewEventPayload>) => {
        handleEventPayload(event.data);
      };

      const storageHandler = (event: StorageEvent) => {
        if (event.key === EVENT_STORAGE_KEY) {
          handleEventPayload(tryParseJson<RenewEventPayload>(event.newValue));
          return;
        }

        if (event.key === LOCK_STORAGE_KEY && !event.newValue) {
          finish('released');
        }
      };

      const poll = window.setInterval(() => {
        if (!this.hasActiveLock()) {
          finish('released');
        }
      }, 350);

      const timeout = window.setTimeout(() => {
        finish('timeout');
      }, timeoutMs);

      const cleanup = () => {
        window.clearInterval(poll);
        window.clearTimeout(timeout);
        window.removeEventListener('storage', storageHandler);
        this.channel?.removeEventListener('message', messageHandler);
      };

      window.addEventListener('storage', storageHandler);
      this.channel?.addEventListener('message', messageHandler);
    });
  }

  public hasActiveLock(): boolean {
    if (!this.isEnabled()) {
      return false;
    }

    this.clearExpiredLock();
    const lock = this.readLock();
    return !!lock && lock.expiresAt > now();
  }

  private resolveTabId(): string {
    if (!isBrowser()) {
      return 'server';
    }

    const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const generated = createId();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, generated);
    return generated;
  }

  private createChannel(): BroadcastChannel | null {
    if (!isBrowser() || typeof BroadcastChannel === 'undefined') {
      return null;
    }

    try {
      return new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return null;
    }
  }

  private readLock(): RenewLockPayload | null {
    return tryParseJson<RenewLockPayload>(localStorage.getItem(LOCK_STORAGE_KEY));
  }

  private writeLock(lock: RenewLockPayload): void {
    localStorage.setItem(LOCK_STORAGE_KEY, JSON.stringify(lock));
  }

  private clearExpiredLock(): void {
    const lock = this.readLock();
    if (!lock) {
      return;
    }

    if (lock.expiresAt <= now()) {
      localStorage.removeItem(LOCK_STORAGE_KEY);
    }
  }

  private publish(type: RenewEventType, lockId: string): void {
    if (!this.isEnabled()) {
      return;
    }

    const payload: RenewEventPayload = {
      type,
      lockId,
      tabId: this.tabId,
      at: now(),
    };

    this.channel?.postMessage(payload);
    localStorage.setItem(EVENT_STORAGE_KEY, JSON.stringify(payload));
  }
}

export const crossTabRenewCoordinator = new CrossTabRenewCoordinator();

export type { RenewWaitResult };
